<template>
  <IntegrationBadgesRow
    :linkedin-url="linkedInUrl"
    :salesforce-url="salesforceUrl"
    :hubspot-url="hubspotUrl"
    :pipedrive-url="pipedriveUrl"
    :leadiq-enriched="hasLeadIQ"
    :size="size"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRef } from 'vue'
import IntegrationBadgesRow from './IntegrationBadgesRow.vue'
import { getSalesforceUrl } from '../services/salesforce'
import { getHubspotUrl, getPipedriveUrl, preloadCrmLinkConfig } from '../services/crmLinks'
import { useAccountBasics, type AccountBasics } from '../hooks/useAccountBasics'

/** Minimal shape a caller can pass in directly, e.g. from a list row that
 *  already has these fields, to skip the per-account basics fetch. */
export type AccountIntegrationBadgesData = Pick<
  AccountBasics,
  'linkedin_url' | 'salesforce_account_id' | 'hubspot_company_id' | 'pipedrive_org_id'
> & { leadiq_enrichment_status?: string | null }

const props = withDefaults(defineProps<{
  accountId: string | number
  /** Pre-fetched fields -- when set, skips useAccountBasics entirely. */
  data?: AccountIntegrationBadgesData | null
  size?: number
}>(), {
  size: 14,
})

const accountIdStr = computed(() => String(props.accountId || '').trim())
const fetched = props.data ? null : useAccountBasics(toRef(accountIdStr))
const account = computed<AccountIntegrationBadgesData | null | undefined>(() => {
  if (props.data) return props.data
  const basics = fetched?.basics.value
  if (!basics) return basics
  return { ...basics, leadiq_enrichment_status: basics.leadiq?.enrichment_status }
})

const crmReady = ref(false)
onMounted(async () => { await preloadCrmLinkConfig(); crmReady.value = true })

const linkedInUrl = computed(() => account.value?.linkedin_url || null)
const salesforceUrl = computed(() =>
  account.value?.salesforce_account_id
    ? getSalesforceUrl({ Id: account.value.salesforce_account_id }, 'Account')
    : null
)
const hubspotUrl = computed(() => {
  void crmReady.value
  return account.value?.hubspot_company_id
    ? getHubspotUrl('company', account.value.hubspot_company_id)
    : null
})
const pipedriveUrl = computed(() => {
  void crmReady.value
  return account.value?.pipedrive_org_id
    ? getPipedriveUrl('organization', account.value.pipedrive_org_id)
    : null
})
const hasLeadIQ = computed(() => account.value?.leadiq_enrichment_status === 'succeeded')
</script>
